// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { ContactMethodCode, SleepConcernCode } from '@polarissleep/shared';
import { SLEEP_CONCERN_NOT_SURE, SLEEP_CONCERN_SOMETHING_ELSE } from '@polarissleep/shared';
import type { OTHER_STATE, ServedStateCode } from './eligibility';
import { SERVED_STATES } from './eligibility';

/** Codes match the self-referral intake Questionnaire, so submitting maps them onto Patient unchanged. */
export const SEX_AT_BIRTH_OPTIONS = [
  { value: 'M', label: 'Male' },
  { value: 'F', label: 'Female' },
  { value: '32570681000036106', label: 'Intersex' },
] as const;

export type SexAtBirthCode = (typeof SEX_AT_BIRTH_OPTIONS)[number]['value'];

export const OTHER_OPTION = 'other';

export const PRONOUN_OPTIONS = [
  { value: 'LA29518-0', label: 'He/Him' },
  { value: 'LA29519-8', label: 'She/Her' },
  { value: 'LA29520-6', label: 'They/Them' },
  { value: OTHER_OPTION, label: 'Other' },
] as const;

export type PronounCode = (typeof PRONOUN_OPTIONS)[number]['value'];

export const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Spanish' },
  { value: OTHER_OPTION, label: 'Other' },
] as const;

export type LanguageCode = (typeof LANGUAGE_OPTIONS)[number]['value'];

/** Offered when the visit state's zone is wrong for the patient, e.g. west Texas or northwest Indiana. */
export const TIME_ZONES = [
  { value: 'America/New_York', label: 'Eastern', abbreviation: 'ET' },
  { value: 'America/Chicago', label: 'Central', abbreviation: 'CT' },
  { value: 'America/Denver', label: 'Mountain', abbreviation: 'MT' },
  { value: 'America/Phoenix', label: 'Mountain – Arizona', abbreviation: 'MST' },
  { value: 'America/Los_Angeles', label: 'Pacific', abbreviation: 'PT' },
  { value: 'America/Anchorage', label: 'Alaska', abbreviation: 'AKT' },
  { value: 'Pacific/Honolulu', label: 'Hawaii', abbreviation: 'HT' },
  { value: 'UTC', label: 'UTC', abbreviation: 'UTC' },
] as const;

export type TimeZoneId = (typeof TIME_ZONES)[number]['value'];

export interface AboutYouAnswers {
  firstName?: string;
  lastName?: string;
  preferredName?: string;
  sexAtBirth?: SexAtBirthCode;
  pronouns?: PronounCode;
  /** Set when `pronouns` is "other". */
  pronounsOther?: string;
  email?: string;
  /** (202) 555-0134, as masked. */
  phone?: string;
  contactMethods?: ContactMethodCode[];
  language?: LanguageCode;
  /** Set when `language` is "other". */
  languageOther?: string;
}

export interface WhereYouLiveAnswers {
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  /** USPS code. Unset until changed; the visit state is used until then. */
  state?: string;
  zip?: string;
  /** IANA zone. Unset until changed; the visit state's zone is used until then. */
  timeZone?: TimeZoneId;
  sleepConcerns?: SleepConcernCode[];
  /** Set when `sleepConcerns` includes "something-else". */
  sleepConcernOther?: string;
}

type VisitState = ServedStateCode | typeof OTHER_STATE | undefined;

/** How headings and errors refer to the patient: the patient themself, or a guardian filling in for them. */
export interface PatientVoice {
  minor: boolean;
  /** Preferred name, else first name, else "the patient". */
  name: string;
}

export function patientVoice(aboutYou: AboutYouAnswers, minor: boolean | undefined): PatientVoice {
  return { minor: !!minor, name: aboutYou.preferredName?.trim() || aboutYou.firstName?.trim() || 'the patient' };
}

const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[a-z]{2,}$/i;

export function isValidEmail(email: string | undefined): boolean {
  return EMAIL_PATTERN.test(email?.trim() ?? '');
}
const ZIP_PATTERN = /^\d{5}(-\d{4})?$/;

export function phoneDigits(phone: string | undefined): string {
  return (phone ?? '').replace(/\D/g, '');
}

export type FieldErrors<T> = Partial<Record<keyof T, string>>;

export function aboutYouErrors(answers: AboutYouAnswers, voice: PatientVoice): FieldErrors<AboutYouAnswers> {
  const whose = voice.minor ? "the patient's" : 'your';
  const email = answers.email?.trim() ?? '';
  const errors: FieldErrors<AboutYouAnswers> = {};
  if (!answers.firstName?.trim()) {
    errors.firstName = `Enter ${whose} legal first name`;
  }
  if (!answers.lastName?.trim()) {
    errors.lastName = `Enter ${whose} legal last name`;
  }
  if (!answers.sexAtBirth) {
    errors.sexAtBirth = 'Choose sex at birth';
  }
  if (answers.pronouns === OTHER_OPTION && !answers.pronounsOther?.trim()) {
    errors.pronounsOther = 'Enter pronouns';
  }
  // A minor may not have an email of their own; the guardian's is collected on the guardian step.
  if (email ? !EMAIL_PATTERN.test(email) : !voice.minor) {
    errors.email = 'Enter a valid email';
  }
  if (phoneDigits(answers.phone).length !== 10) {
    errors.phone = 'Enter a 10-digit US phone number';
  }
  if (!answers.contactMethods?.length) {
    errors.contactMethods = 'Choose at least one';
  }
  if (!answers.language) {
    errors.language = 'Choose a language';
  } else if (answers.language === OTHER_OPTION && !answers.languageOther?.trim()) {
    errors.languageOther = 'Enter a language';
  }
  return errors;
}

function servedState(visitState: VisitState): (typeof SERVED_STATES)[number] | undefined {
  return SERVED_STATES.find((state) => state.code === visitState);
}

/** Home state: the one chosen on this step, else the visit state. */
export function resolveHomeState(answers: WhereYouLiveAnswers, visitState: VisitState): string | undefined {
  return answers.state ?? servedState(visitState)?.code;
}

/** Time zone: the one chosen on this step, else the visit state's. */
export function resolveTimeZone(answers: WhereYouLiveAnswers, visitState: VisitState): TimeZoneId {
  return answers.timeZone ?? servedState(visitState)?.timeZone ?? 'America/New_York';
}

/** "Eastern time (ET)" */
export function timeZoneLabel(timeZone: TimeZoneId): string {
  const zone = TIME_ZONES.find((option) => option.value === timeZone) ?? TIME_ZONES[0];
  return zone.value === 'UTC' ? 'UTC' : `${zone.label} time (${zone.abbreviation})`;
}

export function whereYouLiveErrors(
  answers: WhereYouLiveAnswers,
  visitState: VisitState
): FieldErrors<WhereYouLiveAnswers> {
  const homeState = resolveHomeState(answers, visitState);
  const visit = servedState(visitState);
  const errors: FieldErrors<WhereYouLiveAnswers> = {};
  if (!answers.addressLine1?.trim()) {
    errors.addressLine1 = 'Enter a street address';
  }
  if (!answers.city?.trim()) {
    errors.city = 'Enter a city';
  }
  if (!homeState) {
    errors.state = 'Choose a state';
  } else if (visit && homeState !== visit.code) {
    errors.state = `Must be ${visit.label}, the state chosen for the visit`;
  }
  if (!ZIP_PATTERN.test(answers.zip?.trim() ?? '')) {
    errors.zip = 'Enter a 5-digit ZIP or ZIP+4';
  }
  if (!answers.sleepConcerns?.length) {
    errors.sleepConcerns = 'Choose at least one';
  } else if (answers.sleepConcerns.includes(SLEEP_CONCERN_SOMETHING_ELSE) && !answers.sleepConcernOther?.trim()) {
    errors.sleepConcernOther = 'Describe the concern';
  }
  return errors;
}

/** Applies a checkbox change to the concerns: choosing "I'm not sure" clears the rest, and choosing any other clears it. */
export function nextSleepConcerns(previous: readonly SleepConcernCode[], next: SleepConcernCode[]): SleepConcernCode[] {
  const added = next.filter((code) => !previous.includes(code));
  if (added.includes(SLEEP_CONCERN_NOT_SURE)) {
    return [SLEEP_CONCERN_NOT_SURE];
  }
  return added.length ? next.filter((code) => code !== SLEEP_CONCERN_NOT_SURE) : next;
}
